import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Alert, Row, Col, InputGroup } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faEdit, faTrash, faEye } from '@fortawesome/free-solid-svg-icons';
import StudentService from '../../services/student.service';

const Students = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Estados de busca e filtro
  const [searchTerm, setSearchTerm] = useState('');
  const [cpfFilter, setCpfFilter] = useState('');
  const [enrollmentFilter, setEnrollmentFilter] = useState('');

  // Estados do formulário
  const [formData, setFormData] = useState({
    name: '',
    cpf: '',
    birth_date: null,
    sex: '',
    email: '',
    phone: '',
    is_whatsapp: false,
    active: true,
    responsible_name: '',
    responsible_cpf: '',
    responsible_rg: '',
    responsible_address: '',
    responsible_neighborhood: '',
    responsible_city: '',
    responsible_state: '',
    responsible_address_type: '',
    responsible_phone: '',
    responsible_email: ''
  });

  // Estados de mensagem
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, cpfFilter, enrollmentFilter]);

  const fetchStudents = async (params = {}) => {
    setLoading(true);
    try {
      const data = await StudentService.getAll(params);
      setStudents(data.results || data);
    } catch (error) {
      showAlert('Erro ao carregar alunos.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: 'success' }), 5000);
  };

  const handleSearch = () => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (cpfFilter) params.cpf = cpfFilter.replace(/\D/g, '');
    if (enrollmentFilter === 'not_enrolled') params.not_enrolled = true;
    fetchStudents(params);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCpfFilter('');
    setEnrollmentFilter('');
    fetchStudents();
  };

  const handleOpenModal = (mode = 'create', student = null) => {
    setModalMode(mode);
    setSelectedStudent(student);

    if (mode === 'create') {
      resetForm();
    } else if (mode === 'view') {
      setFormData({
        name: student.name || '',
        cpf: student.cpf || '',
        birth_date: student.birth_date || null,
        sex: student.sex || '',
        email: student.email || '',
        phone: student.phone || '',
        is_whatsapp: student.is_whatsapp || false,
        active: student.active !== undefined ? student.active : true,
        responsible_name: student.responsible_name || '',
        responsible_cpf: student.responsible_cpf || '',
        responsible_rg: student.responsible_rg || '',
        responsible_address: student.responsible_address || '',
        responsible_neighborhood: student.responsible_neighborhood || '',
        responsible_city: student.responsible_city || '',
        responsible_state: student.responsible_state || '',
        responsible_address_type: student.responsible_address_type || '',
        responsible_phone: student.responsible_phone || '',
        responsible_email: student.responsible_email || ''
      });
    } else {
      setFormData({
        name: student.name || '',
        cpf: student.cpf || '',
        birth_date: student.birth_date || null,
        sex: student.sex || '',
        email: student.email || '',
        phone: student.phone || '',
        is_whatsapp: student.is_whatsapp || false,
        active: student.active !== undefined ? student.active : true,
        responsible_name: student.responsible_name || '',
        responsible_cpf: student.responsible_cpf || '',
        responsible_rg: student.responsible_rg || '',
        responsible_address: student.responsible_address || '',
        responsible_neighborhood: student.responsible_neighborhood || '',
        responsible_city: student.responsible_city || '',
        responsible_state: student.responsible_state || '',
        responsible_address_type: student.responsible_address_type || '',
        responsible_phone: student.responsible_phone || '',
        responsible_email: student.responsible_email || ''
      });
    }

    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      cpf: '',
      birth_date: null,
      sex: '',
      email: '',
      phone: '',
      is_whatsapp: false,
      active: true,
      responsible_name: '',
      responsible_cpf: '',
      responsible_rg: '',
      responsible_address: '',
      responsible_neighborhood: '',
      responsible_city: '',
      responsible_state: '',
      responsible_address_type: '',
      responsible_phone: '',
      responsible_email: ''
    });
    setSelectedStudent(null);
  };

  const formatCPFInput = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 11) {
      return cleaned
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return value;
  };

  const formatPhoneInput = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 11) {
      if (cleaned.length <= 10) {
        // Formato: (00) 0000-0000
        return cleaned
          .replace(/(\d{2})(\d)/, '($1) $2')
          .replace(/(\d{4})(\d)/, '$1-$2');
      } else {
        // Formato: (00) 00000-0000
        return cleaned
          .replace(/(\d{2})(\d)/, '($1) $2')
          .replace(/(\d{5})(\d)/, '$1-$2');
      }
    }
    return value;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newValue = value;

    // Aplicar formatação automática para CPF
    if (name === 'cpf' || name === 'responsible_cpf') {
      newValue = formatCPFInput(value);
    }

    // Aplicar formatação automática para telefone
    if (name === 'phone' || name === 'responsible_phone') {
      newValue = formatPhoneInput(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : newValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      showAlert('Nome é obrigatório.', 'warning');
      return;
    }

    // Obter escola do usuário logado
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const schoolId = user.school_users?.[0]?.school?.id;

    if (!schoolId) {
      showAlert('Usuário sem escola associada.', 'danger');
      return;
    }

    setLoading(true);
    try {
      const studentData = {
        ...formData,
        school: schoolId,
        cpf: formData.cpf ? formData.cpf.replace(/\D/g, '') : null,
        responsible_cpf: formData.responsible_cpf ? formData.responsible_cpf.replace(/\D/g, '') : '',
        responsible_rg: formData.responsible_rg || '',
        phone: formData.phone ? formData.phone.replace(/\D/g, '') : '',
        responsible_phone: formData.responsible_phone ? formData.responsible_phone.replace(/\D/g, '') : ''
      };

      if (modalMode === 'create') {
        await StudentService.create(studentData);
        showAlert('Aluno cadastrado com sucesso!', 'success');
      } else {
        await StudentService.update(selectedStudent.id, studentData);
        showAlert('Aluno atualizado com sucesso!', 'success');
      }

      handleCloseModal();
      fetchStudents();
    } catch (error) {
      showAlert(error.response?.data?.message || 'Erro ao salvar aluno.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este aluno?')) {
      return;
    }

    setLoading(true);
    try {
      await StudentService.delete(id);
      showAlert('Aluno excluído com sucesso!', 'success');
      fetchStudents();
    } catch (error) {
      showAlert('Erro ao excluir aluno.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const formatCPF = (cpf) => {
    if (!cpf) return '';
    const cleaned = cpf.replace(/\D/g, '');
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  };

  const formatDate = (date) => {
    if (!date) return '';
    const [year, month, day] = date.split('-');
    return `${day}/${month}/${year}`;
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Alunos</h4>
          <p className="mb-0">Gerencie os alunos da escola</p>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <Button variant="primary" size="sm" onClick={() => handleOpenModal('create')}>
            <FontAwesomeIcon icon={faUserPlus} className="me-2" />
            Novo Aluno
          </Button>
        </div>
      </div>

      {alert.show && (
        <Alert variant={alert.variant} onClose={() => setAlert({ ...alert, show: false })} dismissible>
          {alert.message}
        </Alert>
      )}

      <Card border="light" className="shadow-sm mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Buscar por Nome</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Digite o nome do aluno..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Buscar por CPF</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="000.000.000-00"
                    value={cpfFilter}
                    onChange={(e) => setCpfFilter(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    maxLength={14}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Status Matrícula</Form.Label>
                <Form.Select
                  value={enrollmentFilter}
                  onChange={(e) => setEnrollmentFilter(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="not_enrolled">Sem matrícula</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button variant="secondary" onClick={handleClearFilters} className="w-100">
                Limpar Filtros
              </Button>
            </Col>
          </Row>

          <Table responsive hover className="align-items-center">
            <thead>
              <tr>
                <th>Nome</th>
                <th>CPF</th>
                <th>Data Nascimento</th>
                <th>Email</th>
                <th>Telefone</th>
                <th>Responsável</th>
                <th className="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center">Carregando...</td>
                </tr>
              ) : students.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">Nenhum aluno encontrado</td>
                </tr>
              ) : (
                students.map((student) => (
                  <tr key={student.id}>
                    <td>{student.name}</td>
                    <td>{formatCPF(student.cpf) || '-'}</td>
                    <td>{formatDate(student.birth_date)}</td>
                    <td>{student.email || '-'}</td>
                    <td>{formatPhone(student.phone)}</td>
                    <td>{student.responsible_name || '-'}</td>
                    <td className="text-center">
                      <Button
                        variant="info"
                        size="sm"
                        className="me-2"
                        onClick={() => handleOpenModal('view', student)}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                      <Button
                        variant="warning"
                        size="sm"
                        className="me-2"
                        onClick={() => handleOpenModal('edit', student)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(student.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} backdrop={modalMode === 'edit' ? 'static' : true} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'create' ? 'Novo Aluno' : modalMode === 'view' ? 'Visualizar Aluno' : 'Editar Aluno'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {modalMode === 'view' ? (
              <>
                <h5 className="mb-3">Dados Pessoais</h5>
                <Row className="mb-2">
                  <Col md={8}><strong>Nome Completo:</strong> {formData.name || '-'}</Col>
                  <Col md={4}><strong>CPF:</strong> {formatCPF(formData.cpf) || '-'}</Col>
                </Row>
                <Row className="mb-2">
                  <Col md={4}><strong>Data de Nascimento:</strong> {formatDate(formData.birth_date) || '-'}</Col>
                  <Col md={4}><strong>Sexo:</strong> {formData.sex === 'M' ? 'Masculino' : formData.sex === 'F' ? 'Feminino' : formData.sex === 'O' ? 'Outro' : '-'}</Col>
                </Row>

                <h5 className="mb-3 mt-4">Contato</h5>
                <Row className="mb-2">
                  <Col md={6}><strong>Email:</strong> {formData.email || '-'}</Col>
                  <Col md={6}><strong>Telefone:</strong> {formatPhone(formData.phone) || '-'} {formData.is_whatsapp && '(WhatsApp)'}</Col>
                </Row>

                <h5 className="mb-3 mt-4">Dados do Responsável</h5>
                <Row className="mb-2">
                  <Col md={12}><strong>Nome:</strong> {formData.responsible_name || '-'}</Col>
                </Row>
                <Row className="mb-2">
                  <Col md={6}><strong>CPF:</strong> {formatCPF(formData.responsible_cpf) || '-'}</Col>
                  <Col md={6}><strong>RG:</strong> {formData.responsible_rg || '-'}</Col>
                </Row>
                <Row className="mb-2">
                  <Col md={6}><strong>Email:</strong> {formData.responsible_email || '-'}</Col>
                  <Col md={6}><strong>Telefone:</strong> {formatPhone(formData.responsible_phone) || '-'}</Col>
                </Row>

                <h5 className="mb-3 mt-4">Endereço do Responsável</h5>
                <Row className="mb-2">
                  <Col md={8}><strong>Endereço:</strong> {formData.responsible_address || '-'}</Col>
                  <Col md={4}><strong>Bairro:</strong> {formData.responsible_neighborhood || '-'}</Col>
                </Row>
                <Row className="mb-2">
                  <Col md={6}><strong>Cidade:</strong> {formData.responsible_city || '-'}</Col>
                  <Col md={3}><strong>Estado:</strong> {formData.responsible_state || '-'}</Col>
                  <Col md={3}><strong>Tipo:</strong> {formData.responsible_address_type === 'U' ? 'Urbana' : formData.responsible_address_type === 'R' ? 'Rural' : '-'}</Col>
                </Row>
              </>
            ) : (
              <>
                <h5 className="mb-3">Dados Pessoais</h5>
                <Row>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nome Completo *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>CPF</Form.Label>
                      <Form.Control
                        type="text"
                        name="cpf"
                        value={formData.cpf}
                        onChange={handleInputChange}
                        placeholder="000.000.000-00"
                        maxLength={14}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Data de Nascimento</Form.Label>
                      <Form.Control
                        type="date"
                        name="birth_date"
                        value={formData.birth_date}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sexo *</Form.Label>
                      <Form.Select
                        name="sex"
                        value={formData.sex}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Selecione</option>
                        <option value="M">Masculino</option>
                        <option value="F">Feminino</option>
                        <option value="O">Outro</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <h5 className="mb-3 mt-4">Contato</h5>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Telefone</Form.Label>
                      <Form.Control
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(00) 00000-0000"
                        maxLength={15}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>WhatsApp</Form.Label>
                      <Form.Check
                        type="checkbox"
                        name="is_whatsapp"
                        label="Este número é WhatsApp"
                        checked={formData.is_whatsapp}
                        onChange={handleInputChange}
                        className="mt-2"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h5 className="mb-3 mt-4">Dados do Responsável</h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nome do Responsável *</Form.Label>
                      <Form.Control
                        type="text"
                        name="responsible_name"
                        value={formData.responsible_name}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>CPF do Responsável</Form.Label>
                      <Form.Control
                        type="text"
                        name="responsible_cpf"
                        value={formData.responsible_cpf}
                        onChange={handleInputChange}
                        placeholder="000.000.000-00"
                        maxLength={14}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>RG do Responsável</Form.Label>
                      <Form.Control
                        type="text"
                        name="responsible_rg"
                        value={formData.responsible_rg}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email do Responsável</Form.Label>
                      <Form.Control
                        type="email"
                        name="responsible_email"
                        value={formData.responsible_email}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Telefone do Responsável</Form.Label>
                      <Form.Control
                        type="text"
                        name="responsible_phone"
                        value={formData.responsible_phone}
                        onChange={handleInputChange}
                        placeholder="(00) 00000-0000"
                        maxLength={15}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h5 className="mb-3 mt-4">Endereço do Responsável</h5>
                <Row>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>Endereço</Form.Label>
                      <Form.Control
                        type="text"
                        name="responsible_address"
                        value={formData.responsible_address}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bairro</Form.Label>
                      <Form.Control
                        type="text"
                        name="responsible_neighborhood"
                        value={formData.responsible_neighborhood}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cidade</Form.Label>
                      <Form.Control
                        type="text"
                        name="responsible_city"
                        value={formData.responsible_city}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Estado</Form.Label>
                      <Form.Control
                        type="text"
                        name="responsible_state"
                        value={formData.responsible_state}
                        onChange={handleInputChange}
                        placeholder="UF"
                        maxLength={2}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tipo de Endereço</Form.Label>
                      <Form.Select
                        name="responsible_address_type"
                        value={formData.responsible_address_type}
                        onChange={handleInputChange}
                      >
                        <option value="">Selecione</option>
                        <option value="U">Urbana</option>
                        <option value="R">Rural</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              {modalMode === 'view' ? 'Fechar' : 'Cancelar'}
            </Button>
            {modalMode !== 'view' && (
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            )}
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default Students;
